import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Copy, ShoppingCart, Store, Plus, FolderOpen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "@/lib/clipboard";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, btnGhost, btnPrimary, inputCls } from "@/lib/studioApi";
import { PageSkeleton } from "@/components/studio/StudioUI";

export default function StudioShopping() {
  const [data, setData] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [showBought, setShowBought] = useState(false);
  const [supplier, setSupplier] = useState("");
  const [dossierFilter, setDossierFilter] = useState("");
  const [form, setForm] = useState({ dossier_id: "", label: "", quantity: "1", unit: "", supplier: "" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    studio
      .get(`/materials?include_bought=${showBought}`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Chargement impossible.")));
  }, [showBought]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    studio.get("/dossiers").then(({ data }) => setDossiers(data)).catch(() => {});
  }, []);

  const addMaterial = async () => {
    if (!form.dossier_id || !form.label.trim()) {
      toast.error("Choisissez le chantier et indiquez le matériel.");
      return;
    }
    setAdding(true);
    try {
      const { dossier_id: dossierId, ...material } = form;
      await studio.post(`/dossiers/${dossierId}/materials`, material);
      toast.success("Matériel ajouté au chantier.");
      setForm({ ...form, label: "", quantity: "1", unit: "", supplier: "" });
      load();
    } catch (e) {
      toast.error(apiError(e, "Ajout impossible."));
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (item) => {
    try {
      await studio.put(`/dossiers/${item.dossier_id}/materials/${item.id}`, {
        label: item.label, quantity: item.quantity, unit: item.unit || "",
        supplier: item.supplier || "", reference: item.reference || "",
        price: item.price || "", note: item.note || "", bought: !item.bought,
      });
      load();
    } catch (e) {
      toast.error(apiError(e, "Mise à jour impossible."));
    }
  };

  const removeItem = async (item) => {
    try {
      await studio.delete(`/dossiers/${item.dossier_id}/materials/${item.id}`);
      toast.success("Matériel retiré.");
      load();
    } catch (e) {
      toast.error(apiError(e, "Suppression impossible."));
    }
  };

  const items = (data?.items || [])
    .filter((i) => !supplier || (i.supplier || "") === supplier)
    .filter((i) => !dossierFilter || i.dossier_id === dossierFilter);

  const grouped = items.reduce((acc, item) => {
    const key = item.dossier_id;
    acc[key] = acc[key] || { dossier: item, list: [] };
    acc[key].list.push(item);
    return acc;
  }, {});
  const dossiersWithItems = Object.values(grouped);

  const copyList = () => {
    const lines = items
      .filter((i) => !i.bought)
      .map((i) => `- ${i.label} × ${i.quantity} ${i.unit || ""}${i.supplier ? ` (${i.supplier})` : ""} — ${i.dossier_reference} ${i.client_name}`);
    copyToClipboard(`Liste magasin Devisto\n${lines.join("\n")}`, "Liste copiée.");
  };

  return (
    <StudioLayout
      title="Liste magasin"
      subtitle="Tout le matériel à emporter, tous vos chantiers en cours réunis. Cochez au comptoir, le dossier se met à jour."
      action={
        <button type="button" data-testid="shopping-copy-btn" onClick={copyList} className={btnGhost}>
          <Copy className="w-3.5 h-3.5" /> Copier la liste
        </button>
      }
    >
      {!data ? (
        <PageSkeleton />
      ) : (
        <div className="mt-6 space-y-4">
          <div data-testid="shopping-add-card" className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <h2 className="font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2">
              <Plus className="w-4 h-4 text-[#3A5CC8]" /> Ajouter du matériel à un chantier
            </h2>
            <p className="mt-1 text-xs text-[#5C6A87]">
              Choisissez le dossier concerné (chantier en cours ou devis enregistré) : le matériel rejoint sa liste et apparaît ici.
            </p>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-[1.5fr_1.4fr_0.6fr_0.6fr_1fr_auto] gap-2">
              <select
                data-testid="shopping-add-dossier"
                className={`${inputCls} col-span-2 sm:col-span-1`}
                value={form.dossier_id}
                onChange={(e) => setForm({ ...form, dossier_id: e.target.value })}
              >
                <option value="">Choisir le chantier…</option>
                {dossiers.map((d) => (
                  <option key={d.id} value={d.id}>{`${d.reference} · ${d.client_name} · ${d.status_label}`}</option>
                ))}
              </select>
              <input data-testid="shopping-add-label" className={inputCls} placeholder="Matériel" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
              <input data-testid="shopping-add-quantity" className={inputCls} placeholder="Qté" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              <input data-testid="shopping-add-unit" className={inputCls} placeholder="Unité" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              <input data-testid="shopping-add-supplier" className={inputCls} placeholder="Fournisseur" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
              <button type="button" data-testid="shopping-add-btn" disabled={adding} onClick={addMaterial} className={`${btnPrimary} col-span-2 sm:col-span-1`}>
                Ajouter
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span data-testid="shopping-count" className="inline-flex items-center gap-2 rounded-lg border border-[#3A5CC8]/20 bg-[#EEF1FC] px-4 py-1.5 text-xs font-bold text-[#3A5CC8]">
              <ShoppingCart className="w-3.5 h-3.5" /> {data.to_buy} article(s) à prendre
            </span>
            <button
              type="button"
              data-testid="shopping-toggle-bought"
              onClick={() => setShowBought((v) => !v)}
              className={`rounded-lg border px-4 py-1.5 text-xs font-semibold transition-colors ${
                showBought ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] text-[#5C6A87]"
              }`}
            >
              {showBought ? "Masquer les articles achetés" : "Afficher les articles achetés"}
            </button>
            {!!data.suppliers.length && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="shopping-supplier-all"
                  onClick={() => setSupplier("")}
                  className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                    supplier === "" ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] text-[#5C6A87]"
                  }`}
                >
                  Tous fournisseurs
                </button>
                {data.suppliers.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`shopping-supplier-${s}`}
                    onClick={() => setSupplier(s)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                      supplier === s ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] text-[#5C6A87]"
                    }`}
                  >
                    <Store className="w-3 h-3" /> {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {!!dossiersWithItems.length && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="shopping-dossier-filter-all"
                onClick={() => setDossierFilter("")}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                  dossierFilter === "" ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] text-[#5C6A87]"
                }`}
              >
                Tous les chantiers
              </button>
              {dossiersWithItems.map(({ dossier }) => (
                <button
                  key={dossier.dossier_id}
                  type="button"
                  data-testid={`shopping-dossier-filter-${dossier.dossier_reference}`}
                  onClick={() => setDossierFilter(dossier.dossier_id)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                    dossierFilter === dossier.dossier_id ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] text-[#5C6A87]"
                  }`}
                >
                  <FolderOpen className="w-3 h-3" /> {dossier.dossier_reference}
                </button>
              ))}
            </div>
          )}

          <div data-testid="shopping-list" className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            {!items.length ? (
              <p className="text-sm text-[#5C6A87]">
                Rien à acheter pour l'instant. Ajoutez du matériel ci-dessus ou depuis un dossier de chantier.
              </p>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <div
                    key={`${item.dossier_id}-${item.id}`}
                    data-testid={`shopping-item-${item.id}`}
                    className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 transition-colors duration-200 ${
                      item.bought ? "border-green-200 bg-green-50" : "border-[#E2E7F7] bg-white"
                    }`}
                  >
                    <button
                      type="button"
                      data-testid={`shopping-toggle-${item.id}`}
                      onClick={() => toggle(item)}
                      aria-label="Marquer comme pris"
                    >
                      <CheckCircle2 className={`w-5 h-5 ${item.bought ? "text-green-600" : "text-slate-300"}`} />
                    </button>
                    <span className={`text-sm font-medium ${item.bought ? "text-green-800 line-through" : "text-slate-800"}`}>
                      {item.label}
                    </span>
                    <span className="text-xs font-semibold text-[#5C6A87]">{item.quantity} {item.unit}</span>
                    {item.supplier && <span className="text-xs text-[#93A0BC]">{item.supplier}</span>}
                    <Link
                      to={`/studio/dossiers/${item.dossier_id}`}
                      data-testid={`shopping-dossier-${item.id}`}
                      className="ml-auto text-right"
                    >
                      <span className="block text-[11px] font-bold text-[#3A5CC8]">{item.dossier_reference}</span>
                      <span className="block text-[11px] text-[#93A0BC]">
                        {item.client_name}{item.site_city ? ` · ${item.site_city}` : ""} · {item.dossier_status_label}
                      </span>
                    </Link>
                    <button
                      type="button"
                      data-testid={`shopping-remove-${item.id}`}
                      onClick={() => removeItem(item)}
                      className="text-slate-300 hover:text-red-600 transition-colors"
                      aria-label="Retirer de la liste"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
